import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { messageApi } from '@/api';
import { config } from '@/app/config';
import { useAuth } from '@/hooks/useAuth';

import { RealtimeContext } from './context';

/**
 * The only place in the application that knows a WebSocket exists.
 *
 * It does one thing: turn a frame into a query invalidation. No component ever
 * holds a socket, and no component renders from one — which is what keeps the
 * socket a *nudge* rather than a second source of truth. Everything a frame
 * announces is then read back over HTTP, so a dropped connection costs latency
 * and nothing else.
 *
 * Typing is the single exception, because there is nothing to fetch: it is
 * held in memory here, expires on a timer, and is never written down.
 */

/** How long a typing indicator lives without being renewed. */
const TYPING_TTL_MS = 6000;

/** At most one typing frame per this interval, whatever the keyboard does. */
const TYPING_THROTTLE_MS = 3000;

/** Admin console queries an admin alert can change. Prefix keys. */
const ADMIN_QUEUE_KEYS = [
  'admin-overview',
  'admin-verifications',
  'admin-student-verifications',
  'admin-document-verifications',
  'admin-document-verification-counts',
  'admin-reports',
  'admin-conversations',
  'admin-companies',
  'admin-students',
  'admin-users',
];

/** Reconnect backoff, capped. A tab left open overnight must not spin. */
const RETRY_MS = [1000, 2000, 5000, 10_000, 30_000];

function socketUrl(ticket) {
  const base = new URL(config.apiBaseUrl, window.location.origin);
  const scheme = base.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${base.host}${base.pathname}/realtime/ws?ticket=${encodeURIComponent(ticket)}`;
}

export function RealtimeProvider({ children }) {
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const [connected, setConnected] = useState(false);
  const [typing, setTyping] = useState({});

  const socketRef = useRef(null);
  const attemptRef = useRef(0);
  const closedRef = useRef(false);
  const lastTypingSentRef = useRef(0);

  const handle = useCallback(
    (frame) => {
      switch (frame.type) {
        case 'conversation.updated':
          // The frame says something changed; the truth is fetched.
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
          queryClient.invalidateQueries({ queryKey: ['messages-unread'] });
          queryClient.invalidateQueries({
            queryKey: ['conversation', frame.conversation_id],
          });
          break;
        case 'notification.created':
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
          // The bell's badge; it would otherwise wait for its next poll.
          queryClient.invalidateQueries({ queryKey: ['notification-count'] });
          // An interview answer or a reschedule request changes what the
          // interview lists show, so they refetch rather than wait for a reload.
          queryClient.invalidateQueries({ queryKey: ['student-interviews'] });
          queryClient.invalidateQueries({ queryKey: ['company-interviews'] });
          // A listing submitted for review reaches the admin queue the same way.
          queryClient.invalidateQueries({ queryKey: ['admin-internships'] });
          if (user?.role === 'SUPER_ADMIN') {
            // Admin alerts announce work in a queue — a new account, a
            // verification request, a report, a document — so the console's
            // queues and counts refetch with the bell rather than on reload.
            for (const key of ADMIN_QUEUE_KEYS) {
              queryClient.invalidateQueries({ queryKey: [key] });
            }
          }
          break;
        case 'access.updated':
          // An admin changed what this user may do. The session user carries
          // the permission set every `can()` reads, so it is fetched again.
          void refreshUser().catch(() => {});
          queryClient.invalidateQueries({ queryKey: ['company-access'] });
          break;
        case 'typing.started':
          setTyping((current) => ({
            ...current,
            [frame.conversation_id]: Date.now() + TYPING_TTL_MS,
          }));
          break;
        default:
          // An unrecognised type is ignored, which is what lets a new event
          // ship before the code that reads it.
          break;
      }
    },
    [queryClient, refreshUser, user?.role],
  );

  useEffect(() => {
    if (!user) return undefined;
    closedRef.current = false;

    let retryTimer;

    async function connect() {
      if (closedRef.current) return;
      try {
        const { ticket } = await messageApi.getRealtimeTicket();
        if (closedRef.current) return;

        const socket = new WebSocket(socketUrl(ticket));
        socketRef.current = socket;

        socket.onopen = () => {
          attemptRef.current = 0;
          setConnected(true);
        };
        socket.onmessage = (event) => {
          try {
            handle(JSON.parse(event.data));
          } catch {
            // A frame we cannot read is one frame, not a broken connection.
          }
        };
        socket.onclose = () => {
          setConnected(false);
          socketRef.current = null;
          if (closedRef.current) return;
          const wait = RETRY_MS[Math.min(attemptRef.current, RETRY_MS.length - 1)];
          attemptRef.current += 1;
          retryTimer = window.setTimeout(connect, wait);
        };
        socket.onerror = () => socket.close();
      } catch {
        // Could not even get a ticket. Back off and try again — the polling
        // fallback keeps the product working in the meantime.
        const wait = RETRY_MS[Math.min(attemptRef.current, RETRY_MS.length - 1)];
        attemptRef.current += 1;
        retryTimer = window.setTimeout(connect, wait);
      }
    }

    void connect();

    return () => {
      closedRef.current = true;
      window.clearTimeout(retryTimer);
      socketRef.current?.close();
      socketRef.current = null;
      setConnected(false);
    };
  }, [user, handle]);

  // Expire typing indicators. Nothing tells us when somebody stopped typing —
  // they closed the tab, or thought better of it — so it always expires.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setTyping((current) => {
        const now = Date.now();
        const live = Object.fromEntries(
          Object.entries(current).filter(([, expires]) => expires > now),
        );
        return Object.keys(live).length === Object.keys(current).length ? current : live;
      });
    }, 1500);
    return () => window.clearInterval(timer);
  }, []);

  const sendTyping = useCallback((conversationId) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < TYPING_THROTTLE_MS) return;
    lastTypingSentRef.current = now;
    socket.send(JSON.stringify({ type: 'typing', conversation_id: conversationId }));
  }, []);

  return (
    <RealtimeContext.Provider value={{ connected, typing, sendTyping }}>
      {children}
    </RealtimeContext.Provider>
  );
}
